import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Check, Loader2 } from "lucide-react";
import useSEO from "@/lib/useSEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CAREER_CATEGORIES, CONFIDENTIALITY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSITIONS = [
  "Article Assistant",
  "Qualified Chartered Accountant",
  "Accounting & Tax Professional",
  "Finance & Advisory Professional",
  "General Interest",
];

const field = "w-full bg-white border border-border-subtle px-4 py-3 text-charcoal text-[0.95rem] placeholder:text-charcoal/40 focus:border-navy transition-colors duration-300 outline-none";
const labelCls = "block text-xs font-semibold tracking-wider uppercase text-charcoal/70 mb-2";

const Careers = () => {
  useSEO({
    title: "Careers",
    description:
      "Build your professional journey with Arun Gupta & Co. Opportunities for article assistants, qualified chartered accountants, and accounting, tax and finance professionals in Kanpur.",
    path: "/careers",
  });

  const [openings, setOpenings] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", position: POSITIONS[0], qualification: "", introduction: "" });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/careers/openings`).then((r) => setOpenings(r.data.openings || [])).catch(() => setOpenings([]));
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setCv(null);
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Please upload a PDF, DOC or DOCX file.");
      e.target.value = "";
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("CV must be 8MB or smaller.");
      e.target.value = "";
      return;
    }
    setCv(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2) return toast.error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email address.");
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (cv) data.append("cv", cv);
      await axios.post(`${API}/careers/apply`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Your application has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Careers"
        titleLines={["Build your professional", "journey with us."]}
        intro="Meaningful work, real client exposure and the kind of technical development that comes from a practice where professional responsibility is taken seriously."
        testId="careers-hero"
      />

      {/* Life at the firm */}
      <section className="py-14 lg:py-20" data-testid="careers-life">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl tracking-tight leading-[1.14]">
                A place to learn the craft properly.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-charcoal/85">
                In a boutique practice, you are close to the work that matters. You see engagements
                from beginning to end, you are trusted with responsibility, and you learn from people
                who care about getting it right.
              </p>
              <p className="mt-6 text-base leading-relaxed text-charcoal/75">
                We value continuous learning, technical rigour and the practical judgement that turns
                knowledge into useful advice.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 lg:py-24 border-t border-border-subtle" style={{ background: "#EFEBE1" }} data-testid="careers-categories">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><div className="overline">Where you might fit</div></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 mt-12">
            {CAREER_CATEGORIES.map((c, i) => (
              <Reveal key={c.title} delay={(i % 2) * 0.06}>
                <div className="border-t border-navy/15 pt-6">
                  <h3 className="font-serif text-navy text-2xl lg:text-3xl">{c.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-charcoal/75">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Current openings */}
      <section className="py-16 lg:py-24" data-testid="careers-openings">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><div className="overline">Current Opportunities</div></Reveal>
          <Reveal delay={0.08}>
            {openings.length === 0 ? (
              <div className="mt-8 border-t border-b border-border-subtle py-14 text-center">
                <p className="font-serif text-navy text-3xl lg:text-4xl">No current openings</p>
                <p className="mt-4 text-base text-charcoal/70 max-w-md mx-auto">
                  We are always glad to hear from capable professionals. If your background fits our
                  practice, please introduce yourself below.
                </p>
              </div>
            ) : (
              <div className="mt-8 border-t border-border-subtle">
                {openings.map((o) => (
                  <div key={o.id || o.title} className="py-8 border-b border-border-subtle" data-testid="career-opening">
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                      <h3 className="font-serif text-navy text-2xl lg:text-3xl">{o.title}</h3>
                      {o.location && <span className="overline">{o.location}</span>}
                    </div>
                    {(o.qualification || o.experience) && (
                      <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-sm text-charcoal/70">
                        {o.qualification && <span><span className="text-brass">Qualification:</span> {o.qualification}</span>}
                        {o.experience && <span><span className="text-brass">Experience:</span> {o.experience}</span>}
                      </div>
                    )}
                    {o.description && <p className="mt-4 text-base leading-relaxed text-charcoal/80 max-w-3xl whitespace-pre-line">{o.description}</p>}
                    {o.application && <p className="mt-3 text-sm text-charcoal/60 max-w-3xl whitespace-pre-line">{o.application}</p>}
                  </div>
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* Application form */}
      <section className="py-14 lg:py-20 border-t border-border-subtle" style={{ background: "#17263D" }} data-testid="careers-form-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline">Introduce Yourself</div>
            <h2 className="font-serif text-ivory text-3xl lg:text-4xl tracking-tight mt-5 leading-[1.12]">
              Tell us a little about you.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ivory/70">{CONFIDENTIALITY}</p>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            {done ? (
              <div className="bg-ivory p-10 lg:p-14 text-center" data-testid="careers-success">
                <div className="w-14 h-14 rounded-full border border-brass flex items-center justify-center mx-auto">
                  <Check className="text-brass" size={26} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-navy text-3xl mt-6">Thank you.</h3>
                <p className="mt-4 text-charcoal/75 max-w-md mx-auto">
                  Your application has been received. If your background matches our current
                  requirements, we will be in touch.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-ivory p-6 sm:p-10" data-testid="careers-form" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="c-name">Full Name</label>
                    <input id="c-name" name="full_name" value={form.full_name} onChange={onChange} className={field} placeholder="Your name" data-testid="careers-name-input" required />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="c-email">Email</label>
                    <input id="c-email" name="email" type="email" value={form.email} onChange={onChange} className={field} placeholder="you@example.com" data-testid="careers-email-input" required />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="c-phone">Phone</label>
                    <input id="c-phone" name="phone" value={form.phone} onChange={onChange} className={field} placeholder="+91" data-testid="careers-phone-input" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="c-position">Position / Area of Interest</label>
                    <select id="c-position" name="position" value={form.position} onChange={onChange} className={field} data-testid="careers-position-select">
                      {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className={labelCls} htmlFor="c-qual">Qualification / Experience</label>
                  <input id="c-qual" name="qualification" value={form.qualification} onChange={onChange} className={field} placeholder="e.g. CA Finalist, 2 years' experience" data-testid="careers-qualification-input" />
                </div>
                <div className="mt-5">
                  <label className={labelCls} htmlFor="c-intro">Brief Introduction</label>
                  <textarea id="c-intro" name="introduction" value={form.introduction} onChange={onChange} rows={4} className={field} placeholder="A few lines about yourself and what you're looking for" data-testid="careers-introduction-input" />
                </div>
                <div className="mt-5">
                  <label className={labelCls}>CV Upload <span className="text-charcoal/40 normal-case tracking-normal font-normal">(PDF, DOC, DOCX — optional)</span></label>
                  <label className="flex items-center gap-3 border border-dashed border-navy/30 px-4 py-4 cursor-pointer hover:border-navy transition-colors duration-300" data-testid="careers-cv-label">
                    <UploadCloud size={20} strokeWidth={1.5} className="text-brass" />
                    <span className="text-[0.9rem] text-charcoal/70">{cv ? cv.name : "Choose a file to upload"}</span>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" data-testid="careers-cv-input" />
                  </label>
                </div>
                <button type="submit" className="btn-solid mt-8 w-full sm:w-auto justify-center" disabled={submitting} data-testid="careers-submit-btn">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit Application</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Careers;
